import { useCallback, useRef } from "react";

type AsyncTask<TArgs extends unknown[] = [], TResult = unknown> = (
  ...args: TArgs
) => Promise<TResult>;

/**
 * A custom React hook for managing a queue of asynchronous tasks.
 * It provides a mechanism to enqueue tasks and process them sequentially,
 * ensuring proper execution and cleanup handling for each task.
 *
 * @returns An object containing the `enqueueTask` function:
 * - `enqueueTask(task, taskCleanup?)`: Adds a new task to the queue.
 *    - `task`: A function that returns a promise for the task to be executed.
 *    - `taskCleanup?`: An optional cleanup function that takes the result of the task
 *      and performs any necessary cleanup after the task completes.
 *    - Returns a function to enqueue a cleanup task for the task result.
 *
 * @example
 * const { enqueueTask } = useAsyncTaskQueue<string>();
 *
 * useEffect(() => {
 *   return enqueueTask(
 *     async () => {
 *       console.log("Task started");
 *       await new Promise((resolve) => setTimeout(resolve, 1000));
 *       console.log("Task completed");
 *       return "Task result";
 *     },
 *     async (result) => {
 *       console.log("Cleaning up task:", result);
 *     },
 *   );
 * }, [enqueueTask]);
 */
export function useAsyncTaskQueue<TTaskResult>() {
  const nextTaskId = useRef(0);
  const tasksQueue = useRef<AsyncTask[]>([]);
  const tasksResults = useRef(new Map<number, Promise<TTaskResult>>());
  const drainLock = useRef(false);

  const drainQueue = useCallback(async () => {
    if (drainLock.current) {
      return;
    }
    drainLock.current = true;

    let processedTasks = 0;
    while (tasksQueue.current[processedTasks]) {
      try {
        await tasksQueue.current[processedTasks]?.();
      } catch (e) {
        console.error("[useAsyncTaskQueue]", "Task failed", e);
      }

      processedTasks += 1;
    }
    tasksQueue.current.splice(0, processedTasks);

    drainLock.current = false;
  }, []);

  const enqueueTask = useCallback(
    (
      task: AsyncTask<[], TTaskResult>,
      taskCleanup?: AsyncTask<[TTaskResult]>,
    ) => {
      const id = ++nextTaskId.current;

      tasksQueue.current.push(async () => {
        const taskPromise = task();
        tasksResults.current.set(id, taskPromise);
        return taskPromise;
      });

      void drainQueue();

      return () => {
        tasksQueue.current.push(async () => {
          const result = await tasksResults.current.get(id);
          tasksResults.current.delete(id);
          if (result === undefined) {
            throw new Error(`No task result found for task id ${id}`);
          }
          return taskCleanup?.(result);
        });

        void drainQueue();
      };
    },
    [drainQueue],
  );

  return {
    enqueueTask,
  };
}
